// src/context/PremiumStatusContext.tsx

import React, { createContext, useContext, useEffect, useState } from "react";
import { getAuth, onAuthStateChanged, User } from "firebase/auth";
import {
  getFirestore,
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import app from "../firebase/firebaseConfig";

interface PremiumStatusContextProps {
  isSubscribedStripe: boolean;
  currentRoleStripe: string;
  subscriptionTypeStripe: string;
  productIdStripe: string;
  isLoading: boolean;
}

const PremiumStatusContext = createContext<
  PremiumStatusContextProps | undefined
>(undefined);

export const PremiumStatusProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [currentRole, setCurrentRole] = useState("Guest");
  const [subscriptionType, setSubscriptionType] = useState("");
  const [productId, setProductId] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth(app);

    const unsubscribeAuth = onAuthStateChanged(auth, (user: User | null) => {
      if (!user) {
        // User is logged out, reset all states
        setIsSubscribed(false);
        setCurrentRole("Guest");
        setSubscriptionType("");
        setProductId("");
        setIsLoading(false);
        return;
      }

      // User is logged in, fetch subscription data
      const db = getFirestore(app);
      const subscriptionsRef = collection(
        db,
        "customers",
        user.uid,
        "subscriptions"
      );
      const q = query(
        subscriptionsRef,
        where("status", "in", ["trialing", "active"])
      );

      const unsubscribeSubscription = onSnapshot(q, (snapshot) => {
        let foundActive = false;
        let currentRoleLocal = "Guest";
        let currentTypeLocal = "";
        let currentProductIdLocal = "";

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (!data.items) return; // Ensure items exist
          data.items.forEach((item: any) => {
            const role = item.price.product.metadata?.role;
            const type = item.price.product.metadata?.type;
            const pid = item.price.product.id;

            if (role) {
              currentRoleLocal = role;
              foundActive = true;
            }
            if (type) {
              currentTypeLocal = type;
            }
            if (pid) {
              currentProductIdLocal = pid;
            }
          });
        });

        setCurrentRole(currentRoleLocal);
        setSubscriptionType(currentTypeLocal);
        setProductId(currentProductIdLocal);
        setIsSubscribed(foundActive);
        setIsLoading(false);
      });

      return () => {
        unsubscribeSubscription();
      };
    });

    return () => {
      unsubscribeAuth();
    };
  }, []);

  return (
    <PremiumStatusContext.Provider
      value={{
        isSubscribedStripe: isSubscribed,
        currentRoleStripe: currentRole,
        subscriptionTypeStripe: subscriptionType,
        productIdStripe: productId,
        isLoading,
      }}
    >
      {children}
    </PremiumStatusContext.Provider>
  );
};

export const usePremiumStatus = (): PremiumStatusContextProps => {
  const context = useContext(PremiumStatusContext);
  if (!context) {
    throw new Error(
      "usePremiumStatus must be used within a PremiumStatusProvider"
    );
  }
  return context;
};
